import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
import { GlobalHttpAbortController } from 'src/api/Http/GlobalHttpAbortController';
import { AxiosHttpError, isCancelError } from 'src/api/Http/Http.types';
import { HttpError } from 'src/api/Http/HttpError';
import { deviceFingerprint } from 'src/utils/fingerprint';

import { API_URL } from 'src/env.json';
import { toast } from 'src/components/ui/use-toast';

class Http {
  axios: AxiosInstance;

  post: AxiosInstance['post'];

  get: AxiosInstance['get'];

  put: AxiosInstance['put'];

  delete: AxiosInstance['delete'];

  baseUrl = API_URL;

  private fingerprintReady: Promise<void>;
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  constructor() {
    const defaultHeaders = {
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=utf-8',
      'Access-Control-Expose-Headers': 'Access-Control-*',
      'Access-Control-Allow-Headers':
        'Access-Control-*, Origin, X-Requested-With, Content-Type, Accept',
      'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS, HEAD',
      'Access-Control-Allow-Origin': '*',
      Allow: 'GET, POST, PUT, DELETE, OPTIONS, HEAD',
      crossdomain: true,
    };

    this.axios = axios.create({
      withCredentials: true,
      headers: defaultHeaders,
      signal: GlobalHttpAbortController.signal,
    });

    // Initialize device headers setup (don't await - let it run in background)
    this.fingerprintReady = this.setupDeviceHeaders();

    if (!AuthUtils.isExpired()) {
      this.axios.defaults.headers.common['Authorization'] = `Bearer ${AuthUtils.getJwt().token}`;
    }

    this.post = this.axios.post;
    this.get = this.axios.get;
    this.put = this.axios.put;
    this.delete = this.axios.delete;

    // Add request interceptor to ensure fingerprint is ready
    this.axios.interceptors.request.use(async (request) => {
      // Wait for fingerprint to be ready before making admin requests
      if (request.url?.includes('/api/admin')) {
        try {
          await this.fingerprintReady;
        } catch (error) {
          console.warn('Fingerprint not ready, proceeding with request:', error);
        }
      }
      return request;
    }, this.errorInterceptor);
    
    this.axios.interceptors.response.use((response: AxiosResponse) => {
      // If its json just return data
      if (response.headers['content-type']?.includes('application/json')) {
        return response.data;
      }

      return response;
    }, this.errorInterceptorWithRefresh);
  }

  setBearer(accessToken: string): void {
    this.axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  }

  private async setupDeviceHeaders(): Promise<void> {
    try {
      console.log('[DeviceFingerprint] Generating device fingerprint...');
      const fingerprint = await deviceFingerprint.getFingerprint();
      const deviceName = deviceFingerprint.getDeviceName();
      const userAgent = deviceFingerprint.getUserAgent();
      const ipAddress = deviceFingerprint.getIpAddress();

      // Set device headers for all requests
      this.axios.defaults.headers.common['x-device-fingerprint'] = fingerprint;
      this.axios.defaults.headers.common['x-device-name'] = deviceName;
      this.axios.defaults.headers.common['x-user-agent'] = userAgent;
      this.axios.defaults.headers.common['x-ip-address'] = ipAddress;
      
      console.log('[DeviceFingerprint] Device headers set successfully', {
        fingerprint: fingerprint.substring(0, 8) + '...',
        deviceName,
        userAgent,
      });
    } catch (error) {
      console.error('[DeviceFingerprint] Failed to setup device headers:', error);
      // Even on error, set basic headers from fallback data
      try {
        this.axios.defaults.headers.common['x-device-name'] = deviceFingerprint.getDeviceName();
        this.axios.defaults.headers.common['x-user-agent'] = deviceFingerprint.getUserAgent();
        this.axios.defaults.headers.common['x-ip-address'] = deviceFingerprint.getIpAddress();
        console.warn('[DeviceFingerprint] Using partial device data (fingerprint generation failed)');
      } catch (fallbackError) {
        console.error('[DeviceFingerprint] Complete failure to set device headers:', fallbackError);
      }
      throw error; // Re-throw to signal failure to caller
    }
  }

  errorInterceptor = (axiosError: AxiosHttpError): Promise<HttpError> => {
    const httpError = new HttpError(axiosError);

    if (!isCancelError(httpError)) {
      this.setToastError(httpError);
    }

    return Promise.reject(httpError);
  };

  errorInterceptorWithRefresh = async (axiosError: AxiosHttpError): Promise<any> => {
    const originalRequest = axiosError.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const httpError = new HttpError(axiosError);

    // Handle 401 errors with token refresh
    if (httpError.code === 401 && originalRequest && !originalRequest._retry) {
      // Prevent retry loops
      originalRequest._retry = true;

      try {
        console.log('[Http] 401 detected, attempting token refresh...');
        
        // Get or create refresh promise (prevents multiple simultaneous refresh attempts)
        if (!this.refreshPromise) {
          this.isRefreshing = true;
          this.refreshPromise = this.refreshToken();
        }

        const newToken = await this.refreshPromise;
        
        // Reset refresh state
        this.isRefreshing = false;
        this.refreshPromise = null;

        if (newToken) {
          console.log('[Http] Token refreshed successfully, retrying request');
          // Update authorization header
          this.setBearer(newToken);
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          
          // Retry original request with new token
          return this.axios(originalRequest);
        } else {
          console.error('[Http] Token refresh failed, redirecting to login');
          // Show user-friendly session expiration message
          toast({
            title: 'Session Expired',
            description: 'Your session has expired due to inactivity. Please log in again to continue.',
            variant: 'destructive',
          });
          
          // Redirect to login after a short delay to let user read the message
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
          
          return Promise.reject(httpError);
        }
      } catch (refreshError) {
        console.error('[Http] Token refresh error:', refreshError);
        this.isRefreshing = false;
        this.refreshPromise = null;
        
        // Show user-friendly session expiration message
        if (!isCancelError(httpError)) {
          toast({
            title: 'Session Expired',
            description: 'Your session has expired. Please log in again to continue.',
            variant: 'destructive',
          });
          
          // Redirect to login after showing message
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        }
        return Promise.reject(httpError);
      }
    }

    // For non-401 errors or retry failures, show toast and reject
    if (!isCancelError(httpError)) {
      // If this is a 401 on a retried request, the token refresh succeeded
      // but the request still fails — this is an authorization issue, not session expiry
      if (httpError.code === 401 && originalRequest?._retry) {
        toast({
          title: 'Access Denied',
          description: httpError.message || 'You do not have permission to access this resource. Please contact an administrator if you believe this is an error.',
          variant: 'destructive',
        });
      } else {
        this.setToastError(httpError);
      }
    }

    return Promise.reject(httpError);
  };

  private refreshToken = async (): Promise<string | null> => {
    try {
      // Import Api dynamically to avoid circular dependency
      const { Api } = await import('src/api');
      const response = await Api.auth.refresh();
      
      if (response?.access_token) {
        // Update stored JWT
        AuthUtils.setJwt(response.access_token);
        return response.access_token;
      }
      
      return null;
    } catch (error) {
      console.error('[Http] Failed to refresh token:', error);
      return null;
    }
  };

  convertObjectToQueryString = (params: any): string => {
    return Object.keys(params)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');
  };

  private setToastError = (error: HttpError): void => {
    if (error.code === 422) {
      return;
    }

    // Handle authentication errors (401) - session expired
    if (error.code === 401) {
      toast({
        title: 'Session Expired',
        description: 'Your session has expired. Please log in again to continue.',
        variant: 'destructive',
      });
      return;
    }

    // Handle authorization errors (403) - permission denied / not assigned
    if (error.code === 403) {
      toast({
        title: 'Access Denied',
        description: error.message || 'You do not have permission to perform this action.',
        variant: 'destructive',
      });
      return;
    }

    // Handle timeout errors
    if (error.code === 408 || error.message.toLowerCase().includes('timeout')) {
      toast({
        title: 'Request Timeout',
        description: 'The request took too long to complete. Please check your connection and try again.',
        variant: 'destructive',
      });
      return;
    }

    // Handle network errors
    if (error.code === 0 || error.message.toLowerCase().includes('network')) {
      toast({
        title: 'Connection Error',
        description: 'Unable to connect to the server. Please check your internet connection and try again.',
        variant: 'destructive',
      });
      return;
    }

    // Handle server errors (500-599)
    if (typeof error.code === 'number' && error.code >= 500) {
      toast({
        title: 'Server Error',
        description: 'Something went wrong on our end. Our team has been notified. Please try again later.',
        variant: 'destructive',
      });
      return;
    }

    // Handle other errors with custom message if available
    let message = '',
      title: string | undefined = undefined;

    if (error.message.includes('|')) {
      [title, message] = error.message.split('|');
    } else {
      message = error.message;
    }

    toast({ description: message, title, variant: 'destructive' });
  };
}

export { Http };
